import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import Inspector from '@/components/Inspector';
import ValidationPanel from '@/components/ValidationPanel';
import CapabilityLedger from '@/components/CapabilityLedger';
import EnforcementPanel from '@/components/EnforcementPanel';
import CFSP1Panel from '@/components/CFSP1Panel';
import { Eye, AlertCircle, Zap, ListOrdered, Clock, GitBranch, ArrowRight, Shield } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Context Dock - Inspector Stack (Machine Semantics)
 * 
 * Tabbed stack showing:
 * - Inspector (default) - selection-driven
 * - Capabilities
 * - Validation Errors
 * - Execution Trace
 * 
 * This panel is selection-driven, not mode-driven.
 * It ALWAYS exists (never conditionally not rendered).
 */
const ContextDock = ({ 
  machine, 
  selectedNode,
  validation,
  capabilities,
  machineId,
  onRevalidate,
  simulationTrace = []
}) => {
  const [activeTab, setActiveTab] = useState('inspector');

  const errorCount = validation?.issues?.filter(i => i.severity === 'error').length || 0;
  const warningCount = validation?.issues?.filter(i => i.severity === 'warning').length || 0;
  const traceCount = simulationTrace.length;

  // No machine loaded state
  if (!machine) {
    return (
      <div className="h-full flex flex-col" data-testid="context-dock">
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center text-gray-500">
            <Eye className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No machine selected</p>
            <p className="text-xs mt-1">Select or create a machine to inspect</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" data-testid="context-dock">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
        <div className="border-b border-white/10 px-2 py-1 bg-black/30 flex-shrink-0">
          <TabsList className="bg-transparent w-full grid grid-cols-5 h-8">
            <TabsTrigger 
              value="inspector" 
              className="text-xs data-[state=active]:bg-blue-500/20"
              data-testid="tab-inspector"
            >
              <Eye className="w-3 h-3 mr-1" />
              Inspect
            </TabsTrigger>
            <TabsTrigger 
              value="cfsp1" 
              className="text-xs data-[state=active]:bg-cyan-500/20"
              data-testid="tab-cfsp1"
            >
              <Shield className="w-3 h-3 mr-1" />
              CFSP
            </TabsTrigger>
            <TabsTrigger 
              value="errors" 
              className="text-xs data-[state=active]:bg-blue-500/20 relative"
              data-testid="tab-errors"
            >
              <AlertCircle className="w-3 h-3 mr-1" />
              Errors
              {errorCount > 0 && (
                <Badge 
                  variant="destructive" 
                  className="ml-1 h-4 w-4 p-0 text-[10px] flex items-center justify-center"
                >
                  {errorCount}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger 
              value="enforce" 
              className="text-xs data-[state=active]:bg-yellow-500/20"
              data-testid="tab-enforce"
            >
              <Zap className="w-3 h-3 mr-1" />
              Effects
            </TabsTrigger>
            <TabsTrigger 
              value="trace" 
              className="text-xs data-[state=active]:bg-blue-500/20"
              data-testid="tab-trace"
            >
              <ListOrdered className="w-3 h-3 mr-1" />
              Trace
              {traceCount > 0 && (
                <Badge 
                  variant="outline" 
                  className="ml-1 h-4 px-1 text-[10px]"
                >
                  {traceCount}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="inspector" className="flex-1 m-0 overflow-auto">
          <Inspector machine={machine} selectedNode={selectedNode} />
        </TabsContent>

        <TabsContent value="cfsp1" className="flex-1 m-0 overflow-hidden">
          <CFSP1Panel machineId={machineId} />
        </TabsContent>

        <TabsContent value="errors" className="flex-1 m-0 overflow-auto p-3">
          <ValidationPanel
            machineId={machineId}
            validation={validation}
            onRevalidate={onRevalidate}
            compact
          />
        </TabsContent>

        <TabsContent value="enforce" className="flex-1 m-0 overflow-hidden">
          <EnforcementPanel
            machineId={machineId}
            machine={machine}
            capabilities={capabilities}
          />
        </TabsContent>

        <TabsContent value="trace" className="flex-1 m-0 overflow-hidden flex flex-col">
          <TemporalTracePanel machineId={machineId} simulationTrace={simulationTrace} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

/**
 * Temporal Trace Panel - Time as First-Class Dimension
 * Shows event ordering, causal chains, and execution history
 */
const TemporalTracePanel = ({ machineId, simulationTrace = [] }) => {
  const [temporalData, setTemporalData] = useState(null);
  const apiUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (machineId) {
      fetch(`${apiUrl}/api/machines/${machineId}/temporal/summary`)
        .then(r => r.ok ? r.json() : null)
        .then(data => setTemporalData(data))
        .catch(() => {});
    }
  }, [machineId, apiUrl]);

  const traceCount = simulationTrace.length;
  const eventCount = temporalData?.total_events || 0;

  return (
    <div className="h-full flex flex-col">
      {/* Summary Header */}
      <div className="p-2 border-b border-white/10 bg-black/20 flex-shrink-0">
        <div className="flex items-center justify-between text-[10px]">
          <div className="flex items-center space-x-3">
            <div className="flex items-center text-gray-400">
              <Clock className="w-3 h-3 mr-1" />
              <span>{eventCount} temporal events</span>
            </div>
            {temporalData?.causal_chains > 0 && (
              <div className="flex items-center text-purple-400">
                <GitBranch className="w-3 h-3 mr-1" />
                <span>{temporalData.causal_chains} chains</span>
              </div>
            )}
          </div>
          {temporalData?.hash && (
            <Badge variant="outline" className="text-[9px] px-1 h-4 font-mono">
              {temporalData.hash}
            </Badge>
          )}
        </div>
      </div>

      {/* Trace Content */}
      <ScrollArea className="flex-1">
        <div className="p-2 space-y-1">
          {traceCount === 0 && eventCount === 0 ? (
            <div className="text-center py-8 text-xs text-gray-500">
              <Clock className="w-6 h-6 mx-auto mb-2 opacity-50" />
              <p>No execution trace yet</p>
              <p className="text-[10px] mt-1 text-gray-600">
                Run simulation to see temporal events
              </p>
            </div>
          ) : (
            simulationTrace.map((entry, idx) => (
              <TraceEntry key={idx} entry={entry} index={idx} />
            ))
          )}
        </div>
      </ScrollArea>
    </div>
  );
};

/**
 * Individual Trace Entry
 */
const TraceEntry = ({ entry, index }) => {
  const hasEffects = entry.effects?.length > 0;
  const hasGuards = entry.guards_evaluated?.length > 0;
  
  return (
    <div className={cn(
      "p-2 rounded text-xs border",
      entry.transition_taken 
        ? "bg-white/5 border-white/10" 
        : "bg-yellow-500/5 border-yellow-500/20"
    )}>
      {/* Header */}
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center space-x-2">
          <span className="text-gray-500 font-mono text-[10px]">[{entry.step_index ?? index}]</span>
          <Badge variant="outline" className={cn(
            "text-[10px] px-1 h-4",
            entry.transition_taken ? "text-blue-400 border-blue-500/30" : "text-yellow-400 border-yellow-500/30"
          )}>
            {entry.event_dispatched}
          </Badge>
        </div>
        <span className="text-[9px] text-gray-600">
          {entry.timestamp ? new Date(entry.timestamp).toLocaleTimeString() : ''}
        </span>
      </div>
      
      {/* State Transition */}
      <div className="flex items-center text-gray-400 font-mono text-[11px]">
        <span>{entry.from_state}</span>
        <ArrowRight className="w-3 h-3 mx-1" />
        <span className={entry.transition_taken ? "text-green-400" : "text-gray-500"}>
          {entry.to_state}
        </span>
      </div>
      
      {/* Guards */}
      {hasGuards && (
        <div className="mt-1 flex items-center gap-1 flex-wrap">
          {entry.guards_evaluated.map((g, i) => (
            <Badge 
              key={i} 
              variant="outline" 
              className={cn(
                "text-[9px] px-1 h-4",
                g.passed ? "text-green-400 border-green-500/30" : "text-red-400 border-red-500/30"
              )}
            >
              {g.guard_name}: {g.passed ? '✓' : '✗'}
            </Badge>
          ))}
        </div>
      )}
      
      {/* Effects */}
      {hasEffects && (
        <div className="mt-1 space-y-0.5">
          {entry.effects.map((eff, i) => (
            <div key={i} className="flex items-center text-[10px]">
              <Zap className={cn(
                "w-3 h-3 mr-1",
                eff.status === 'executed' && "text-green-400",
                eff.status === 'deferred' && "text-yellow-400",
                eff.status === 'denied' && "text-red-400"
              )} />
              <span className="font-mono">{eff.effect_name}</span>
              <span className={cn(
                "ml-1",
                eff.status === 'executed' && "text-green-400",
                eff.status === 'deferred' && "text-yellow-400",
                eff.status === 'denied' && "text-red-400"
              )}>
                ({eff.status})
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContextDock;
